import React, { useState, useEffect, useMemo } from 'react';
import { Route, Routes, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import SettingsPanel from '../../components/settings/SettingsPanel';
import notify from '../../utils/notify';
import {
  fetchOrganizationSettings,
  saveOrganizationSettings,
  ORGANIZATION_SETTINGS_KEY,
} from '../../services/settingsAPI';
import { invalidateSettingsGraph } from '../../utils/invalidateSettingsGraph';
import {
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  ClockIcon,
  CreditCardIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  LanguageIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

import { 
  GeneralSettings, 
  NotificationSettings, 
  SecuritySettings, 
  BusinessHours,
  BillingSettings,
  CourseSettings,
  ClassroomSettings,
  TrainerSettings,
  CertificationSettings
} from '../../components/settings';
import { useLanguage } from '../../contexts/LanguageContext';

const SettingsPage = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(location.pathname.split('/').pop() || 'general');
  const [settings, setSettings] = useState({});
  const queryClient = useQueryClient();

  const { data: settingsData } = useQuery(
    ORGANIZATION_SETTINGS_KEY,
    fetchOrganizationSettings,
    { staleTime: 30_000, refetchOnWindowFocus: true }
  );

  useEffect(() => {
    if (settingsData) {
      setSettings(settingsData);
    }
  }, [settingsData]);

  useEffect(() => {
    const seg = location.pathname.split('/').pop() || 'general';
    setActiveTab(seg);
  }, [location.pathname]);

  const handleSettingsChange = async (newSettings) => {
    setSettings(newSettings);
    try {
      await saveOrganizationSettings(newSettings);
      await invalidateSettingsGraph(queryClient);
    } catch (error) {
      console.warn('Failed to save settings:', error);
      notify.error('Impossible de sauvegarder les paramètres');
    }
  };

  const handleHasChanges = () => {};

  const tabs = useMemo(
    () => [
      { id: 'general', name: t('settingsPage.tabGeneral'), desc: t('settingsPage.tabGeneralDesc'), icon: Cog6ToothIcon, component: GeneralSettings },
      { id: 'notifications', name: t('settingsPage.tabNotifications'), desc: t('settingsPage.tabNotificationsDesc'), icon: BellIcon, component: NotificationSettings },
      { id: 'security', name: t('settingsPage.tabSecurity'), desc: t('settingsPage.tabSecurityDesc'), icon: ShieldCheckIcon, component: SecuritySettings },
      { id: 'hours', name: t('settingsPage.tabHours'), desc: t('settingsPage.tabHoursDesc'), icon: ClockIcon, component: BusinessHours },
      { id: 'billing', name: t('settingsPage.tabBilling'), desc: t('settingsPage.tabBillingDesc'), icon: CreditCardIcon, component: BillingSettings },
      { id: 'courses', name: t('settingsPage.tabCourses'), desc: t('settingsPage.tabCoursesDesc'), icon: AcademicCapIcon, component: CourseSettings },
      { id: 'classrooms', name: t('settingsPage.tabClassrooms'), desc: t('settingsPage.tabClassroomsDesc'), icon: BuildingLibraryIcon, component: ClassroomSettings },
      { id: 'trainers', name: t('settingsPage.tabTrainers'), desc: t('settingsPage.tabTrainersDesc'), icon: UserGroupIcon, component: TrainerSettings },
      { id: 'certifications', name: t('settingsPage.tabCertifications'), desc: t('settingsPage.tabCertificationsDesc'), icon: DocumentTextIcon, component: CertificationSettings },
    ],
    [t]
  );

  const currentTab = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  const sharedProps = {
    settings,
    onSettingsChange: handleSettingsChange,
    onHasChanges: handleHasChanges,
  };

  const renderTab = (TabComponent, needsShared = true) => (
    <SettingsPanel
      title={currentTab.name}
      description={currentTab.desc}
      icon={currentTab.icon}
    >
      {needsShared ? <TabComponent {...sharedProps} /> : <TabComponent />}
    </SettingsPanel>
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('settingsPage.title')} description={t('settingsPage.description')} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] lg:items-start">
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:sticky lg:top-20"
        >
          <UiCard className="overflow-hidden">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('settingsPage.sections')}
            </h2>
            <nav className="flex flex-col gap-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.id}
                  to={`/settings/${tab.id}`}
                  className={clsx(
                    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'bg-forma-600 text-white shadow-md shadow-forma-600/20'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-50'
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{tab.name}</span>
                </Link>
              ))}
            </nav>

            <div className="mt-8 border-t border-slate-200 pt-5 dark:border-slate-700">
              <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {t('settingsPage.resources')}
              </h3>
              <div className="flex flex-col gap-1">
                <Link
                  to="#"
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                >
                  <DevicePhoneMobileIcon className="h-5 w-5 shrink-0" />
                  <span>{t('settingsPage.mobileApp')}</span>
                </Link>
                <Link
                  to="#"
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                >
                  <LanguageIcon className="h-5 w-5 shrink-0" />
                  <span>{t('settingsPage.languages')}</span>
                </Link>
                <Link
                  to="#"
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                >
                  <DocumentTextIcon className="h-5 w-5 shrink-0" />
                  <span>{t('settingsPage.helpGuide')}</span>
                </Link>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {t('settingsPage.contactSupport')}
              </button>
            </div>
          </UiCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="min-w-0"
        >
          <UiCard padding={false} className="overflow-hidden">
            <Routes>
              <Route index element={renderTab(GeneralSettings)} />
              <Route path="general" element={renderTab(GeneralSettings)} />
              <Route path="notifications" element={renderTab(NotificationSettings)} />
              <Route path="security" element={renderTab(SecuritySettings)} />
              <Route path="hours" element={renderTab(BusinessHours)} />
              <Route path="billing" element={renderTab(BillingSettings)} />
              <Route path="courses" element={renderTab(CourseSettings, false)} />
              <Route path="classrooms" element={renderTab(ClassroomSettings, false)} />
              <Route path="trainers" element={renderTab(TrainerSettings)} />
              <Route path="certifications" element={renderTab(CertificationSettings, false)} />
            </Routes>
          </UiCard>
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage;
